<template>
  <div v-no-focus:[isLoading]="isLoading" :class="classList">
    <div class="st-news-card__wrapper">
      <div
        class="st-news-card__spinner"
        v-if="isLoading"
        :aria-busy="isLoading"
        aria-live="polite"
      >
        <StSpinner :loading-message="loadingMessage" />
      </div>
      <img
        v-if="imageUrl"
        :loading="isAsync ? 'lazy' : 'eager'"
        :decoding="isAsync ? 'async' : 'auto'"
        :src="imageUrl"
        :alt="imageAlt"
      />
      <div class="st-news-card__content-section">
        <div class="st-news-card__headline-container">
          <StHeadline
            v-if="heading"
            :level="headingLevel"
            class="st-news-card__headline"
          >
            <a v-if="link" :href="link" class="st-news-card__link">
              <span class="st-news-card__headline-text">{{ heading }}</span>
              <StIcon
                class="st-news-card__link-icon"
                name="chevron"
                :icon-props="{ color: 'var(--interactive-color-secondary)' }"
              />
            </a>
            <span v-else class="st-news-card__headline-text">{{
              heading
            }}</span>
          </StHeadline>
        </div>
        <p class="st-news-card__sub-heading" v-if="publishDate">
          Publicerad {{ publishDate }}
        </p>
        <p v-if="content" class="st-news-card__content">
          {{ content }}
        </p>
        <div>
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { StHeadline, StSpinner, StIcon } from "@/index";
import { noFocus } from "../../helpers/no-focus";
import { defineProps, computed } from "vue";

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: "Laddar...",
  },
  hideOverflow: {
    type: Boolean,
    default: true,
  },
  imageUrl: {
    type: String,
    default: "",
  },
  imageAlt: {
    type: String,
    default: "",
  },
  heading: {
    type: String,
    default: "",
  },
  headingLevel: {
    type: Number,
    default: 3,
  },
  publishDate: {
    type: String,
    default: "",
  },
  content: {
    type: String,
    default: "",
  },
  link: {
    type: String,
    default: "",
  },
  linkTitle: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    validator(value) {
      return ["sm", "md"].includes(value);
    },
    default: "md",
  },
  type: {
    type: String,
    validator(value) {
      return ["primary", "secondary"].includes(value);
    },
    default: "primary",
  },
  isAsync: {
    type: Boolean,
    default: false,
  },
});

const vNoFocus = noFocus;

const classList = computed(() => {
  return [
    "st-news-card",
    props.hideOverflow && "st-news-card--is-overflow-hidden",
    props.size && `st-news-card--${props.size}`,
    props.type && `st-news-card--${props.type}`,
  ];
});
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-news-card {
  max-width: rem-calc(496);
  font-family: var(--font-primary);
  display: flex;
  border-radius: rem-calc(12);
  background-color: var(--background-color-secondary);

  &:hover {
    background-color: var(--bigarrå-100);
    color: var(--greyscale-900);
  }

  &:focus {
    border: rem-calc(1) solid var(--greyscale-900);
  }

  &:hover,
  &:focus {
    box-shadow: 0 rem-calc(4) rem-calc(14) 0 rgba(0, 0, 0, 0.05);
  }

  &:focus-within {
    outline: rem-calc(2) solid var(--border-color-quaternary);
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
    width: 100%;

    img {
      width: 100%;
      height: rem-calc(369);
      object-fit: cover;
    }
  }

  &__spinner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: rem-calc(12);
    background-color: rgba(106, 106, 106, 0.203);
    backdrop-filter: blur(rem-calc(5));
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  &__content-section {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: rem-calc(32);
    border: rem-calc(1) solid var(--border-color-quinary);
    border-radius: 0 0 rem-calc(12) rem-calc(12);
  }

  &__headline-container {
    display: flex;
    align-items: center;
    gap: rem-calc(8);
  }

  &__headline {
    width: 100%;
    color: var(--text-color-primary);
    :deep(.st-headline--text) {
      line-height: normal !important;
    }
    a {
      font-family: var(--font-secondary);
      color: var(--text-color-primary);
      text-decoration: none;
      outline: none;
      &::after {
        content: "";
        position: absolute;
        z-index: 2;
        border-radius: rem-calc(12);
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
      }
    }
  }

  &__link {
    display: flex;
    justify-content: space-between;
  }

  &__headline-text {
    @include truncate(2);
  }

  &__link-icon {
    vertical-align: middle;
    margin-left: rem-calc(4);
    transform: rotate(-90deg);
  }

  &__sub-heading {
    font-size: rem-calc(14);
    margin: rem-calc(8) 0 0 0;
    color: var(--text-color-primary);
  }

  &__content {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    @include truncate(3);
    color: var(--text-color-primary);
    margin: rem-calc(16) 0;
  }

  &--secondary {
    background-color: var(--container-color-tertiary);
  }

  &--is-overflow-hidden {
    overflow: hidden;
  }

  &--sm {
    max-width: rem-calc(360);

    img {
      height: rem-calc(160);
    }

    .st-news-card__content-section {
      padding: rem-calc(18) rem-calc(24);
    }

    .st-news-card__link {
      display: flex;
    }

    .st-news-card__headline-text {
      font-size: rem-calc(18);
    }

    .st-news-card__content {
      margin: rem-calc(4) 0;
    }

    .st-news-card__sub-heading {
      margin: rem-calc(4) 0 0 0;
    }

    .st-news-card__content {
      @include truncate(2);
    }
  }
}
</style>
